<template>
    <b-container fluid class="p-0">
        <!-- Message d'erreur -->
        <b-alert 
            :show="errorMessage !== ''" 
            variant="danger" 
            dismissible 
            @dismissed="errorMessage = ''" 
            class="mb-3">
            <font-awesome-icon icon="fa-solid fa-triangle-exclamation" class="mr-2" />
            {{ errorMessage }}
        </b-alert>

        <!-- Message de prévention de la compression des fichiers trop volumineux -->
        <b-alert 
            :show="compressionMessages.length > 0" 
            variant="warning" 
            dismissive 
            @dismissed="compressionMessages = []" 
            class="mb-3">
            <div v-for="(message, index) in compressionMessages" :key="index" class="small">
                {{ message }}
            </div>
        </b-alert>

        <!-- Sélection de fichiers -->
        <b-form-group>
            <b-form-file
                v-model="tempFile"
                multiple
                @input="handleFileUpload"
                :placeholder="$t('AddResource')"
                :drop-placeholder="$t('AddResource')"
                :browse-text="$t('Browse')"
                :accept="types.join(', ')"
                :variant="Variant"
                class="bg-white">
                <template slot="file-name" slot-scope="names">
                    <b-badge :variant="Variant">{{ names[0] }}</b-badge>
                    <span v-if="names.length > 1" class="small text-muted ml-2">
                        + {{ names.length - 1 }} {{ $t('OtherFiles') }}
                    </span>
                </template>
            </b-form-file>
        </b-form-group>

        <!-- Fichiers sélectionnés -->
        <b-col v-if="files.length > 0" cols="12">
            <h5 class="mb-3 border-bottom pb-2">{{ $t('SelectedFiles') }} ({{ files.length }})</h5>

            <!-- Toolbar de suppression groupée -->
            <b-row class="mb-2 mx-0">
                <b-col class="p-0 d-flex align-items-center">
                    <b-form-checkbox
                        :checked="allSelected"
                        :indeterminate="someSelected && !allSelected"
                        @change="toggleAllSelection"
                        class="mr-3">
                        {{ $t('SelectAll') }}
                    </b-form-checkbox>
                </b-col>
                <b-col class="p-0 d-flex justify-content-end">
                    <b-button
                        v-if="selectedIds.length > 0"
                        @click="removeSelectedFiles"
                        variant="outline-danger"
                        pill
                        size="sm"
                        v-b-tooltip.hover
                        :title="$t('DeleteSelected')">
                        <font-awesome-icon icon="fa-solid fa-trash" class="pr-1"/>
                        {{ $t('DeleteSelected') }} ({{ selectedIds.length }})
                    </b-button>
                </b-col>
            </b-row>
            <b-list-group shadow-sm>
                <b-list-group-item 
                    v-for="(file, index) in files" 
                    :key="index"
                    class="d-flex justify-content-start align-items-center py-2 px-3 border-left-0 border-right-0">
                    <b-form-checkbox
                        :checked="selectedIds.includes(file.Id)"
                        @change="toggleSelection(file.Id)"
                        class="mr-3"/>
                    <b-button 
                        @click="removeFile(index)"
                        variant="link" 
                        class="text-danger p-0 mr-3"
                        v-b-tooltip.hover
                        :title="$t('Delete')">
                        <font-awesome-icon icon="fa-solid fa-xmark"/>
                    </b-button>
                    <div class="d-flex align-items-center overflow-hidden">
                        <font-awesome-icon 
                            :icon="getFileIcon(file.Name)" 
                            class="mr-3" 
                            :class="'text-' + Variant" 
                            style="font-size: 1.2rem; width: 20px;" />
                        <div class="d-flex flex-column">
                            <span class="text-truncate font-weight-bold">{{ file.Name }}</span>
                        </div>
                    </div>
                </b-list-group-item>
            </b-list-group>
        </b-col>

        <!-- Aucun fichier sélectionné -->
        <b-col v-else cols="12" class="text-center py-5 border rounded bg-light">
            <font-awesome-icon icon="fa-solid fa-cloud-arrow-up" size="3x" class="text-muted mb-3" />
            <p class="text-muted mb-0 font-italic">{{ $t('NoFilesSelected') }}</p>
        </b-col>
		<!-- Fenetre generique de suivi de l'upload de fichiers (store UploadStore) -->
		<UploadProgressDialog />
    </b-container>
</template>

<script lang="ts">
import { Component, InjectReactive, Prop, PropSync, Vue } from 'vue-property-decorator';
import locale from '../locales/SMA.json';
import { Resource } from '@/types/Administration/SMA/SMACertificate';
import { PDFDocument } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';
import UploadProgressDialog from '@/components/commons/UploadProgressDialog.vue';
// Le type PDFDocumentProxy n'est pas exporte par l'entree principale de pdfjs-dist
import { PDFDocumentProxy } from 'pdfjs-dist/types/display/api';
// Exécute le worker pdf.js dans le fil principal (voir pdf.worker.entry.js)
import 'pdfjs-dist/build/pdf.worker.entry';

/**
 * Composant de formulaire d'ajout de ressources (fichiers) pour un certificat
 * Permet de sélectionner un ou plusieurs fichiers, de les afficher avec une icône et une taille lisible, et de les soumettre via un événement
 */
@Component({
    components: {
        UploadProgressDialog
    },
    i18n : { messages: locale }
})
export default class AddResource extends Vue {

    @InjectReactive()
    public Variant!: string;

    @PropSync('Files', { default: () => [] })
    public files!: Resource[];

    @Prop()
    public types!: string[];

    // Limite de taille par fichier : 5 Mo par défaut (exprimé en octets)
    @Prop({ default: 5 * 1024 * 1024 })
    public maxFileSize!: number;

    // Taille maximale acceptable d'un fichier : au-delà, la compression ne fonctionne pas correctement
    private readonly maxUploadableFileSize: number = 15 * 1024 * 1024;

    private tempFile: File | File[] | null = null;
    
    // Variable pour stocker le message d'erreur
    private errorMessage: string = '';

    // Messages de prévention de la compression des fichiers trop volumineux
    private compressionMessages: string[] = [];

    // Identifiants des fichiers sélectionnés pour une suppression groupée
    private selectedIds: string[] = [];

    /**
     * Indique si tous les fichiers sont sélectionnés
     */
    private get allSelected(): boolean {
        return this.files.length > 0 && this.selectedIds.length === this.files.length;
    }

    /**
     * Indique si au moins un fichier est sélectionné
     */
    private get someSelected(): boolean {
        return this.selectedIds.length > 0;
    }

    /**
     * Sélection / désélection d'un fichier
     * @param id Identifiant du fichier
     */
    private toggleSelection(id: string): void {
        if (this.selectedIds.includes(id)) {
            this.selectedIds = this.selectedIds.filter(x => x !== id);
        } else {
            this.selectedIds = [...this.selectedIds, id];
        }
    }

    /**
     * Sélection / désélection de tous les fichiers
     */
    private toggleAllSelection(): void {
        this.selectedIds = this.allSelected ? [] : this.files.map(file => file.Id);
    }

    /**
     * Suppression groupée des fichiers sélectionnés
     */
    private removeSelectedFiles(): void {
        this.files = this.files.filter(file => !this.selectedIds.includes(file.Id));
        this.selectedIds = [];
    }

    /**
     * Retourne l'icône FontAwesome correspondante à l'extension du fichier
     */
    private getFileIcon(fileName: string): string {
        const extension = fileName.split('.').pop()?.toLowerCase();
        
        switch (extension) {
            case 'pdf':
                return 'fa-solid fa-file-pdf';
            case 'csv':
                return 'fa-solid fa-file-csv';
            case 'xlsx':
            case 'xls':
                return 'fa-solid fa-file-excel';
            case 'txt':
                return 'fa-solid fa-file-lines';
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
            case 'svg':
            case 'webp':
                return 'fa-solid fa-file-image';
            default:
                return 'fa-solid fa-file-alt';
        }
    }

    /**
     * Upload de fichiers, gère à la fois les sélections uniques et multiples
     * @param newFiles - Fichier ou liste de fichiers sélectionnés
     */
    private async handleFileUpload(newFiles: File | File[]): Promise<void> {
        if (!newFiles) return;
        const incoming = Array.isArray(newFiles) ? newFiles : [newFiles];
        
        // On réinitialise l'erreur à chaque nouvelle sélection
        this.errorMessage = '';

        // La compression des fichiers trop volumineux peut prendre du temps :
        // affichage du masque de chargement avec l'avancement du traitement
        const needsProcessing = incoming.some(file => this.isPdf(file) && file.size > this.maxFileSize);

        try {
            for (let i = 0; i < incoming.length; i++) {
                const file = incoming[i];

                if (needsProcessing) {
                    this.$store.dispatch("ShowLoadingMask", `${this.$t("CompressingFiles")} (${i + 1}/${incoming.length}) : ${file.name}`);
                }

                // REJET DIRECT : au-delà du seuil maximal, la compression ne fonctionne pas correctement
                if (file.size > this.maxUploadableFileSize) {
                    const maxUploadableMb = (this.maxUploadableFileSize / 1024 / 1024).toFixed(0);
                    this.errorMessage = `Le fichier "${file.name}" dépasse la taille maximale de ${maxUploadableMb} Mo et ne peut pas être ajouté.`;
                    continue;
                }

                // VÉRIFICATION DE LA TAILLE DU FICHIER
                if (file.size > this.maxFileSize) {
                    const maxMb = (this.maxFileSize / 1024 / 1024).toFixed(2);

                    if (this.isPdf(file)) {
                        await this.tryAddCompressedPdf(file, maxMb);
                    } else {
                        // Vous pouvez aussi utiliser votre système i18n : this.$t('FileTooLarge', { file: file.name, size: maxMb })
                        this.errorMessage = `Le fichier "${file.name}" est trop volumineux. La taille maximale autorisée est de ${maxMb} Mo.`;
                    }

                    continue;
                }

                this.addFile(file);
            }
        } finally {
            if (needsProcessing) {
                this.$store.dispatch("HideLoadingMask");
            }

            this.tempFile = null;
        }
    }

    /**
     * Ajout d'un fichier à la liste des ressources du mandat.
     * Le fichier brut est attaché de manière non énumérable : il n'est donc pas sérialisé
     * dans le JSON du mandat lors du save, mais uploadé par lots en multipart via "AddResources".
     * @param file Fichier à ajouter
     */
    private addFile(file: File): void {
        const resource: Resource = new Resource({ 
            Id: Date.now().toString() + '-' + Math.random().toString(36).substr(2, 5),
            Name: file.name.split('.').slice(0, -1).join('.'),
            Extension: '.' + file.name.split('.').pop(),
            Type: "7",
            Data: ''
        });

        Object.defineProperty(resource, 'File', {
            value: file,
            enumerable: false,
            writable: true,
            configurable: true
        });

        this.files.push(resource);
    }

    /**
     * Tentative d'ajout d'un fichier PDF trop volumineux après compression.
     * Un message de prévention est affiché lorsqu'une compression est appliquée au fichier.
     * @param file Fichier PDF à compresser
     * @param maxMb Taille maximale autorisée (Mo), pour l'affichage des messages
     */
    private async tryAddCompressedPdf(file: File, maxMb: string): Promise<void> {
        try {
            const compressed = await this.compressPdf(file);

            if (compressed == null || compressed.size > this.maxFileSize) {
                this.errorMessage = `Le fichier "${file.name}" est trop volumineux malgré la compression (${this.formatSize(file.size)}). La taille maximale autorisée est de ${maxMb} Mo.`;
                return;
            }

            this.compressionMessages.push(`"${file.name}" a été compressé (${this.formatSize(file.size)} → ${this.formatSize(compressed.size)}) pour respecter la limite de ${maxMb} Mo.`);

            this.addFile(compressed);
        } catch (error) {
            console.error('Erreur lors de la compression du fichier :', error);
            this.errorMessage = `La compression du fichier "${file.name}" a échoué. La taille maximale autorisée est de ${maxMb} Mo.`;
        }
    }

    /**
     * Indique si le fichier est un PDF
     * @param file Fichier
     */
    private isPdf(file: File): boolean {
        return file.name.toLowerCase().endsWith('.pdf') || file.type === 'application/pdf';
    }

    /**
     * Formatage d'une taille en octets en Mo
     * @param bytes Taille en octets
     */
    private formatSize(bytes: number): string {
        return (bytes / 1024 / 1024).toFixed(2) + ' Mo';
    }

    /**
     * Compression d'un fichier PDF pour respecter la taille maximale autorisée :
     * 1. optimisation structurelle sans perte (pdf-lib), le texte reste sélectionnable ;
     * 2. rastérisation progressive des pages en JPEG (pdf.js) si l'étape 1 ne suffit pas.
     * @param file Fichier PDF à compresser
     * @returns Fichier compressé, ou null si la taille cible n'est pas atteinte
     */
    private async compressPdf(file: File): Promise<File | null> {
        const originalBytes = new Uint8Array(await file.arrayBuffer());

        try {
            const document = await PDFDocument.load(originalBytes, { ignoreEncryption: true });
            const optimized = await document.save({ useObjectStreams: true });

            if (optimized.byteLength <= this.maxFileSize) {
                return new File([optimized], file.name, { type: 'application/pdf' });
            }
        } catch (error) {
            console.warn('Optimisation structurelle impossible, tentative de rastérisation :', error);
        }

        const attempts = [
            { scale: 1.5, quality: 0.75 },
            { scale: 1.2, quality: 0.6 },
            { scale: 1.0, quality: 0.5 },
            { scale: 0.8, quality: 0.45 }
        ];

        const pdf = await pdfjsLib.getDocument({ data: originalBytes.slice() }).promise;

        try {
            for (const attempt of attempts) {
                const compressed = await this.rasterizePdf(pdf, attempt.scale, attempt.quality);

                if (compressed && compressed.byteLength <= this.maxFileSize) {
                    return new File([compressed], file.name, { type: 'application/pdf' });
                }
            }
        } finally {
            pdf.destroy();
        }

        return null;
    }

    /**
     * Rastérisation des pages d'un PDF en images JPEG puis reconstruction du document
     * @param pdf Document pdf.js
     * @param scale Résolution de rendu des pages
     * @param quality Qualité JPEG (0-1)
     * @returns Octets du PDF reconstruit, ou null en cas d'échec
     */
    private async rasterizePdf(pdf: PDFDocumentProxy, scale: number, quality: number): Promise<Uint8Array | null> {
        const output = await PDFDocument.create();

        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
            const page = await pdf.getPage(pageNumber);

            const baseViewport = page.getViewport({ scale: 1 });

            const renderViewport = page.getViewport({ scale: scale });

            const canvas = document.createElement('canvas');
            canvas.width = Math.floor(renderViewport.width);
            canvas.height = Math.floor(renderViewport.height);

            const context = canvas.getContext('2d');

            if (!context) {
                return null;
            }

            context.fillStyle = '#FFFFFF';
            context.fillRect(0, 0, canvas.width, canvas.height);

            await page.render({ canvasContext: context, viewport: renderViewport }).promise;

            const base64Image = canvas.toDataURL('image/jpeg', quality).split(',')[1];
            const image = await output.embedJpg(base64Image);

            const newPage = output.addPage([baseViewport.width, baseViewport.height]);

            newPage.drawImage(image, {
                x: 0,
                y: 0,
                width: baseViewport.width,
                height: baseViewport.height
            });
        }

        return output.save({ useObjectStreams: true });
    }

    /**
     * Suppresion d'un fichier de la liste
     * @param index index du fichier à supprimer
     */
    private removeFile(index: number): void {
        const removedId: string = this.files[index]?.Id;
        this.files.splice(index, 1);

        if (removedId) {
            this.selectedIds = this.selectedIds.filter(id => id !== removedId);
        }
    }
}
</script>